import styled from 'styled-components';
import {
  SUBSIDY_CATEGORIES,
  SubsidyCategoryType,
  SUBSIDY_CATEGORY_MAP,
} from '../../../../../constants/subsidy/subsidyCategory.constant';
import { Icon } from '../../../../../components/common/icon/Icon';
import { ampli } from '../../../../../ampli';
import { IconSizeType, SvgFileNameType } from '../../../../../interfaces/Element.interfaces';

// Import PNG Icons
import { useDraggableScroll } from '../../../../../hooks/scroll/useDraggableScroll';

import appleIcon from '../../../../../assets/images/subsidy/icon-apple.svg';
import moneyIcon from '../../../../../assets/images/subsidy/icon-money.svg';
import pawIcon from '../../../../../assets/images/subsidy/icon-paw.svg';
import boxIcon from '../../../../../assets/images/subsidy/icon-box.svg';
import cropIcon from '../../../../../assets/images/subsidy/icon-crop.svg';
import tractorIcon from '../../../../../assets/images/subsidy/icon-tracktor.svg';
import houseIcon from '../../../../../assets/images/subsidy/icon-house.svg';
import userIcon from '../../../../../assets/images/subsidy/icon-user.svg';

interface SubsidyCategoryFilterProps {
  selectedCategory: SubsidyCategoryType;
  onCategoryChange: (category: SubsidyCategoryType) => void;
}

// Icon Mapping
const CATEGORY_ICON_MAP: Record<string, string> = {
  직불금: moneyIcon,
  '농기계·자재': tractorIcon,
  시설: houseIcon,
  맞춤지원: userIcon,
  식량: cropIcon,
  '과수·원예': appleIcon,
  축산: pawIcon,
  '유통·판매': boxIcon,
};

export const SubsidyCategoryFilter = ({ selectedCategory, onCategoryChange }: SubsidyCategoryFilterProps) => {
  const { ref, events } = useDraggableScroll<HTMLDivElement>();

  return (
    <Container ref={ref} {...events}>
      {SUBSIDY_CATEGORIES.map((category) => {
        const iconSrc = CATEGORY_ICON_MAP[category];
        const isSelected = selectedCategory === category;

        return (
          <CategoryChip
            key={category}
            isSelected={isSelected}
            onClick={() => {
              onCategoryChange(category);

              const tags =
                category === '전체'
                  ? []
                  : (SUBSIDY_CATEGORY_MAP[category] as unknown as { id: string; name: string }[]);

              ampli.pvmClickSubsidyCategoryTab({
                subsidy_category: category,
                subsidy_tag_ids: tags.map((tag) => tag.id),
                subsidy_tag_names: tags.map((tag) => tag.name),
              });
            }}
          >
            {/* Render Icon if exists */}
            {iconSrc && (
              <IconWrapper>
                <PngIcon src={iconSrc} alt={category} />
              </IconWrapper>
            )}
            <CategoryText isSelected={isSelected}>{category}</CategoryText>
          </CategoryChip>
        );
      })}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 8px;
  padding: 12px 0 20px 0px;

  cursor: grab;
  &:active {
    cursor: grabbing;
  }
  /* Added left padding for better initial scroll state */

  /* Hide scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const CategoryChip = styled.button<{ isSelected: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 100px;

  background-color: ${(props) => (props.isSelected ? '#F0F4FF' : '#FFFFFF')};
  border: 1px solid ${(props) => (props.isSelected ? '#445EFF' : '#ECECF3')};

  cursor: pointer;
  transition: all 0.2s ease;

  &:active {
    transform: scale(0.98);
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
`;

const PngIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const CategoryText = styled.span<{ isSelected: boolean }>`
  font-family: Pretendard;
  font-size: 14px;
  font-weight: ${(props) => (props.isSelected ? '600' : '500')};
  color: ${(props) => (props.isSelected ? '#445EFF' : '#505363')};
  line-height: normal;
`;
